import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoDirective, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { appendToRmaBaseUrl } from '@rma/generic/ng/util';
import { MatErrorOverrideComponent, MatFormErrorComponent } from '@rma/generic/ui/controls/mat-error';
import { ServerValidationService } from '@rma/generic/util/server-validation';
import { PublicProfileContextService } from '@rma/public/accounts/user';
import { catchError, EMPTY, map, Observable, switchMap, take } from 'rxjs';
import { MarketReportApiService } from '../../data-access/market-report-api/market-report-api.service';
import { MarketReport, MarketReportCommunicationRequest, MarketReportCommunicationType } from '../../domain';
import { MarketReportContact } from '../../domain/market-report-property-details';

export interface MarketReportCommunicationModalData {
  type: MarketReportCommunicationType;
  marketReport: MarketReport;
}

interface DefaultMessageTranslations {
  subject: string;
  message: string;
  smsMessage: string;
  signature: string;
}

interface MarketReportForm {
  content: FormControl<string>;
  email?: FormControl<string>;
  subject?: FormControl<string>;
  phone?: FormControl<string>;
}

@Component({
  selector: 'rma-market-report-communication-form',
  templateUrl: './market-report-communication-form.component.html',
  styleUrls: ['./market-report-communication-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogContent,
    MatErrorOverrideComponent,
    MatFormErrorComponent,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslocoDirective,
    TranslocoModule,
  ],
})
export class MarketReportCommunicationFormComponent {
  private readonly profile = inject(PublicProfileContextService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly marketReportApiService = inject(MarketReportApiService);
  private readonly matDialogRef = inject<MatDialogRef<MarketReportCommunicationFormComponent>>(MatDialogRef);
  private readonly modalData = inject<MarketReportCommunicationModalData>(MAT_DIALOG_DATA);
  private readonly rmaBaseUrl = inject(appendToRmaBaseUrl);
  private readonly serverValidationService = inject(ServerValidationService);
  private readonly translationService = inject(TranslocoService);

  public type: MarketReportCommunicationType;
  public marketReport: MarketReport;

  public form = this.createForm(this.modalData.type);
  public marketReportCommunicationTypeEnum = MarketReportCommunicationType;
  public defaultMessageTranslations: Observable<string[]>;

  private listingReportUrl: string;

  constructor() {
    this.type = this.modalData.type;
    this.marketReport = this.modalData.marketReport;
    this.listingReportUrl = this.rmaBaseUrl(`market-report?code=${this.marketReport.listingReportCode}`);

    this.selectContact(this.marketReport.propertyDetails?.contacts[0]);
  }

  public selectContact(contact: MarketReportContact) {
    this.getDefaultMessage(contact)
      .pipe(take(1))
      .subscribe((defaultMessage) => {
        let content: string;
        if (this.type === MarketReportCommunicationType.sms) {
          content = `${defaultMessage.smsMessage}\n\n${this.listingReportUrl}`;
        } else {
          content = this.marketReport.customMessage ?? defaultMessage.message;
        }

        content += `\n\n${defaultMessage.signature}`;

        this.form.patchValue({
          email: contact.email,
          phone: contact.mobile,
          subject: defaultMessage.subject,
          content: content,
        });
      });
  }

  public getDefaultMessage(contact: MarketReportContact): Observable<DefaultMessageTranslations> {
    const translationKeys = [
      'marketReports.communication.default-subject',
      'marketReports.communication.default-message',
      'marketReports.communication.sms.default-message',
      'marketReports.communication.default-message-signature',
    ];

    const mergeFields = {
      contactName: contact.fullName,
      address: this.marketReport.propertyDetails.streetAddress,
      agentName: this.marketReport.profile.fullName ?? '',
      agentPhone: this.marketReport.profile.contactNumber ?? '',
      agency:
        this.marketReport.profile.agencyName !== undefined
          ? this.marketReport.profile.agentCode
            ? this.marketReport.profile.agencyName
            : ''
          : '',
    };

    return this.translationService.selectTranslate(translationKeys, mergeFields).pipe(
      map((translations) => ({
        subject: translations[0],
        message: translations[1].replace(/\\n/g, '\n'),
        smsMessage: translations[2].replace(/\\n/g, '\n'),
        signature: translations[3].replace(/\\n/g, '\n'),
      })),
    );
  }

  public send() {
    if (!this.form.valid) {
      return;
    }

    const request: MarketReportCommunicationRequest = {
      subject: this.form.get('subject')?.value ?? '',
      content: this.form.get('content')?.value ?? '',
      address:
        this.type === MarketReportCommunicationType.email ? (this.form.get('email')?.value ?? '') : (this.form.get('phone')?.value ?? ''),
    };

    return this.profile.profileIdentifier$
      .pipe(
        switchMap(({ profileType, profileCode }) =>
          this.marketReportApiService.sendComms(this.marketReport.listingReportId, this.type, profileType, profileCode, request),
        ),
        catchError((error) => {
          this.serverValidationService.handleApiErrors(error.error, this.form);
          return EMPTY;
        }),
      )
      .subscribe(() => this.matDialogRef.close(true));
  }

  public cancel() {
    this.matDialogRef.close();
  }

  private createForm(type: MarketReportCommunicationType) {
    const formGroup = new FormGroup<MarketReportForm>({
      content: this.fb.control('', {
        validators: [Validators.required],
        updateOn: 'blur',
      }),
    });

    if (type === MarketReportCommunicationType.email) {
      formGroup.addControl(
        'email',
        this.fb.control('', {
          validators: [Validators.required, Validators.email],
          updateOn: 'change',
        }),
      );

      formGroup.addControl(
        'subject',
        this.fb.control('', {
          validators: [Validators.required],
          updateOn: 'blur',
        }),
      );
    }

    if (type === MarketReportCommunicationType.sms) {
      formGroup.addControl(
        'phone',
        this.fb.control('', {
          validators: [Validators.required],
          updateOn: 'change',
        }),
      );
    }

    return formGroup;
  }
}
